import { useEffect, useMemo, useState } from "react";
import { GREEN_STATIONS } from "../stations/greenStations";
import { PURPLE_STATIONS } from "../stations/purpleStations";
import { YELLOW_STATIONS } from "../stations/yellowStations";
import yellowData from "../stations/yellowSchedule.json";
import purpleData from "../stations/purpleSchedule.json";
import greenData from "../stations/greenSchedule.json";
import { t as i18n } from "../i18n";

export default function Favorites({ theme = "light", language = "en" }) {
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth < 768);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const central = i18n(language);
	const T = {
		favourites: central.favourites,
		noFav: central.noFav,
		addFav: central.addFav,
		addFavStop: central.addFavStop,
		line: central.line,
		stop: central.stop,
		cancel: central.cancel,
		save: central.save,
		arriving: central.arriving,
		select: central.select,
		purpleLine: central.purpleLine,
		yellowLine: central.yellowLine,
		greenLine: central.greenLine,
	};

	const stopsByLine = useMemo(
		() => ({
			purple: PURPLE_STATIONS,
			yellow: YELLOW_STATIONS,
			green: GREEN_STATIONS,
		}),
		[]
	);

	const lineColors = {
		purple: "#800080",
		yellow: "#EAB308",
		green: "#16A34A",
	};

	const getStationLabel = (station) => {
		if (!station) return "";
		if (language === "hi") return station.name_hi || station.name;
		if (language === "kn") return station.name_kn || station.name;
		return station.name;
	};

	const [favorites, setFavorites] = useState(() => {
		const saved = localStorage.getItem("favorites");
		return saved ? JSON.parse(saved) : [];
	});

	useEffect(() => {
		localStorage.setItem("favorites", JSON.stringify(favorites));
	}, [favorites]);

	const [modalOpen, setModalOpen] = useState(false);
	const [selectedLine, setSelectedLine] = useState("purple");
	const [selectedStop, setSelectedStop] = useState("");
	const [selectedTowards, setSelectedTowards] = useState("");

	const addFavorite = () => {
		if (!selectedStop || !selectedTowards || favorites.length >= 5) {
			setModalOpen(false);
			return;
		}
		const exists = favorites.some(
			(f) => f.line === selectedLine && f.stop === selectedStop && f.towards === selectedTowards
		);
		if (exists) {
			setModalOpen(false);
			setSelectedStop("");
			setSelectedTowards("");
			return;
		}
		setFavorites([...favorites, { line: selectedLine, stop: selectedStop, towards: selectedTowards }]);
		setModalOpen(false);
		setSelectedStop("");
		setSelectedTowards("");
	};

	const removeFavorite = (favToRemove) => {
		setFavorites(
			favorites.filter(
				(f) =>
					!(
						f.line === favToRemove.line &&
						f.stop === favToRemove.stop &&
						f.towards === favToRemove.towards
					)
			)
		);
	};

	// ==== ETA calculation ====
	const [etas, setEtas] = useState({});
	const IST_OFFSET_MIN = 330;
	const DAY_MS = 24 * 60 * 60 * 1000;
	const hhmmssToMs = (t) => {
		const [hh, mm, ss] = t.split(":").map((x) => parseInt(x, 10));
		return ((hh * 60 + mm) * 60 + (ss || 0)) * 1000;
	};
	const nowWithinDayIST = () => {
		const n = Date.now() + IST_OFFSET_MIN * 60 * 1000;
		const d = new Date(n);
		return (
			((d.getUTCHours() * 60 + d.getUTCMinutes()) * 60 + d.getUTCSeconds()) *
			1000 +
			d.getUTCMilliseconds()
		);
	};
	const nextEtaMinutes = (times) => {
		if (!times || !times.length) return null;
		const nowMs = nowWithinDayIST();
		const list = times
			.map((t) => (typeof t === "string" ? t : t.time))
			.filter(Boolean)
			.map(hhmmssToMs)
			.sort((a, b) => a - b);
		const idx = list.findIndex((ms) => ms >= nowMs);
		const target = idx >= 0 ? list[idx] : list[0] + DAY_MS;
		const etaMs = target - nowMs;
		const mins = Math.max(0, Math.ceil(etaMs / 60000));
		return mins;
	};

	useEffect(() => {
		const compute = () => {
			const result = {};
			for (const fav of favorites) {
				const stations = stopsByLine[fav.line] || [];
				const station = stations.find((s) => s.name === fav.stop);
				const key = `${fav.line}|${fav.stop}|${fav.towards}`;
				if (!station || stations.length === 0) {
					result[key] = null;
					continue;
				}
				const firstName = getStationLabel(stations[0]);
				const lastName = getStationLabel(stations[stations.length - 1]);
				const idx = stations.findIndex((s) => s.name === fav.stop);
				let toLast = null;
				let toFirst = null;
				if (fav.line === "yellow") {
					const stationId = (station.station_id || "").trim().toUpperCase();
					const yellowUp = yellowData?.["UP Line"]?.[stationId]?.trips || [];
					const yellowDown = yellowData?.["DN Line"]?.[stationId]?.trips || [];
					toLast = nextEtaMinutes(yellowUp);
					toFirst = nextEtaMinutes(yellowDown);
				} else if (fav.line === "purple") {
					const stationId = (station.station_id || "").trim().toUpperCase();
					const purpleUp = purpleData?.["UP Line"]?.[stationId]?.trips || [];
					const purpleDown = purpleData?.["DN Line"]?.[stationId]?.trips || [];
					toLast = nextEtaMinutes(purpleUp);
					toFirst = nextEtaMinutes(purpleDown);
				} else if (fav.line === "green") {
					const stationId = (station.station_id || "").trim().toUpperCase();
					const greenUp = greenData?.["UP Line"]?.[stationId]?.trips || [];
					const greenDown = greenData?.["DN Line"]?.[stationId]?.trips || [];
					toLast = nextEtaMinutes(greenUp);
					toFirst = nextEtaMinutes(greenDown);
				} else {
					toLast = nextEtaMinutes(station.upSchedule);
					toFirst = nextEtaMinutes(station.downSchedule);
				}
				result[key] = { toFirst, toLast, idx, firstName, lastName };
			}
			setEtas(result);
		};
		compute();
		const id = setInterval(compute, 15000);
		return () => clearInterval(id);
	}, [favorites, stopsByLine]);

	const etaRow = (station, minutes) => {
		const labelText = getStationLabel(station);
		let txt = "--";
		if (minutes != null) {
			if (minutes < 1) {
				txt = T.arriving;
			} else if (minutes < 60) {
				txt = `${minutes} min`;
			} else {
				const hrs = Math.floor(minutes / 60);
				const mins = minutes % 60;
				txt = mins === 0 ? `${hrs} hr${hrs > 1 ? "s" : ""}` : `${hrs} hr${hrs > 1 ? "s" : ""} ${mins} min`;
			}
		}
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 8,
					fontSize: 13,
					whiteSpace: "nowrap",
				}}
			>
				<span
					style={{
						flex: 1,
						minWidth: 0,
						paddingTop: 13,
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					→ {labelText}
				</span>
				<span style={{ fontWeight: 600, whiteSpace: "nowrap", paddingTop: 13, }}>{txt}</span>
			</div>
		);
	};

	// ==== Layout container ====
	const containerStyle = {
		marginBottom: 0,
		display: "flex",
		flexDirection: isMobile ? "column" : "row",
		gap: "12px",
		overflowY: isMobile ? "auto" : "hidden",
		overflowX: isMobile ? "hidden" : "auto",
		padding: "10px",
		scrollbarWidth: "none",
		msOverflowStyle: "none",
		WebkitOverflowScrolling: "touch",
	};

	return (
		<section style={containerStyle}>
			{favorites.length === 0 && (
				<div
					style={{
						flex: "0 0 180px",
						height: "100px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: 14,
						fontWeight: 500,
						color: theme === "dark" ? "#ccc" : "#555",
					}}
				>
					{T.noFav}
				</div>
			)}

			{favorites.map((fav) => {
				const key = `${fav.line}|${fav.stop}|${fav.towards}`;
				const info = etas[key];
				const col = lineColors[fav.line] || "#999";
				const stations = stopsByLine[fav.line] || [];
				const idx = info?.idx ?? stations.findIndex((s) => s.name === fav.stop);
				const firstName = info?.firstName ?? stations[0]?.name ?? "Terminal A";
				const lastName = info?.lastName ?? stations[stations.length - 1]?.name ?? "Terminal B";
				const showToLast = idx > -1 && idx !== stations.length - 1;
				const showToFirst = idx > 0;

				return (
					<div
						key={key}
						style={{
							flex: isMobile ? "unset" : "0 0 250px",
							height: "100px",
							width: isMobile ? "100%" : "250px",
							background: theme === "dark" ? "#1b1b1b" : "#ffffff",
							color: theme === "dark" ? "#fff" : "#111",
							borderRadius: 14,
							padding: "12px",
							display: "flex",
							flexDirection: "column",
							boxShadow:
								theme === "dark"
									? "0 3px 10px rgba(0,0,0,0.8)"
									: "0 5px 20px rgba(0,0,0,0.3)",
						}}
					>
						{/* Header */}
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
								<div style={{ width: "4px", height: "18px", borderRadius: "2px", background: col }} />
								<span style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>
									{getStationLabel(stations.find((s) => s.name === fav.stop))}
									{/* {fav.towards && (
										<span style={{ fontSize: 12, fontWeight: 400, marginLeft: 6, opacity: 0.8 }}>
											→ {getStationLabel(stations.find((s) => s.name === fav.towards))}
										</span>
									)} */}
								</span>
							</div>
							<button
								onClick={() => removeFavorite(fav)}
								style={{
									background: "transparent",
									border: "none",
									color: theme === "dark" ? "#aaa" : "#444",
									cursor: "pointer",
									fontSize: 16,
								}}
							>
								×
							</button>
						</div>

						{/* Direction ETA */}
						<div style={{ fontSize: 13, marginTop: 8 }}>
							{fav.towards === firstName && showToFirst &&
								etaRow(stations[0], info?.toFirst ?? null)}
							{fav.towards === lastName && showToLast &&
								etaRow(stations[stations.length - 1], info?.toLast ?? null)}
						</div>
					</div>
				);
			})}

			{favorites.length < 5 && (
				<div
					onClick={() => setModalOpen(true)}
					style={{
						flex: isMobile ? "unset" : "0 0 180px",
						height: "100px",
						border: theme === "dark" ? "2px dashed #555" : "2px dashed #aaa",
						borderRadius: 10,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "pointer",
						color: theme === "dark" ? "#fff" : "#111",
						background: theme === "dark" ? "#1e1e1e" : "#fafafa",
					}}
				>
					<span style={{ fontSize: 14, fontWeight: 500 }}>+ {T.addFav}</span>
				</div>
			)}

			{/* Modal */}
			{modalOpen && (
				<div
					onClick={() => setModalOpen(false)}
					style={{
						position: "fixed",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						background: theme === "dark" ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.35)",
						backdropFilter: "blur(6px)",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						zIndex: 3000,
					}}
				>
					<div
						onClick={(e) => e.stopPropagation()}
						style={{
							background: theme === "dark" ? "#1e1e1e" : "#fff",
							color: theme === "dark" ? "#fff" : "#111",
							padding: 20,
							borderRadius: 12,
							minWidth: 280,
							position: "relative",
							boxShadow: theme === "dark"
								? "0 0 12px rgba(255,255,255,0.1)"
								: "0 0 12px rgba(0,0,0,0.2)",
						}}
					>
						<button
							onClick={() => setModalOpen(false)}
							style={{
								position: "absolute",
								top: 10,
								right: 10,
								background: "transparent",
								border: "none",
								color: theme === "dark" ? "#fff" : "#111",
								fontSize: 18,
								cursor: "pointer",
							}}
						>
							×
						</button>

						<h4 style={{ marginTop: 0 }}>{T.addFavStop}</h4>

						<div style={{ marginBottom: 12 }}>
							<div style={{ marginBottom: 6, fontWeight: 600 }}>{T.line}</div>
							<div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
								{["purple", "yellow", "green"].map((line) => {
									const active = selectedLine === line;
									const color = lineColors[line] || "#888";
									const label = line === "purple" ? T.purpleLine : line === "yellow" ? T.yellowLine : T.greenLine;
									return (
										<button
											key={line}
											onClick={() => {
												setSelectedLine(line);
												setSelectedStop("");
												setSelectedTowards("");
											}}
											style={{
												padding: "10px 12px",
												borderRadius: 10,
												border: active ? `2px solid ${color}` : "1px solid #ccc",
												background: active ? "#f7faff" : "#fff",
												color: "#111",
												cursor: "pointer",
											}}
										>
											<span
												style={{
													width: 10,
													height: 10,
													borderRadius: "50%",
													background: color,
													display: "inline-block",
													marginRight: 6,
												}}
											/>
											{label}
										</button>
									);
								})}
							</div>
						</div>

						{/* Stop dropdown */}
						<label style={{ display: "block", marginBottom: 12 }}>
							{T.stop}:{" "}
							<select
								value={selectedStop}
								onChange={(e) => setSelectedStop(e.target.value)}
								style={{
									width: "100%",
									marginTop: 6,
									padding: "8px",
									borderRadius: 6,
									border: theme === "dark" ? "1px solid #444" : "1px solid #ccc",
									background: theme === "dark" ? "#2a2a2a" : "#fff",
									color: theme === "dark" ? "#fff" : "#111",
								}}
							>
								<option value="">{T.select}</option>
								{(stopsByLine[selectedLine] || []).map((stop) => (
									<option key={stop.name} value={stop.name}>
										{getStationLabel(stop)}
									</option>
								))}
							</select>
						</label>

						{/* Towards dropdown */}
						<label style={{ display: "block", marginBottom: 12 }}>
							Towards:{" "}
							<select
								value={selectedTowards}
								onChange={(e) => setSelectedTowards(e.target.value)}
								style={{
									width: "100%",
									marginTop: 6,
									padding: "8px",
									borderRadius: 6,
									border: theme === "dark" ? "1px solid #444" : "1px solid #ccc",
									background: theme === "dark" ? "#2a2a2a" : "#fff",
									color: theme === "dark" ? "#fff" : "#111",
								}}
							>
								<option value="">Select direction</option>
								{(() => {
									const stations = stopsByLine[selectedLine] || [];
									if (stations.length === 0) return null;
									const first = stations[0];
									const last = stations[stations.length - 1];
									return (
										<>
											<option value={first.name}>Towards {getStationLabel(first)}</option>
											<option value={last.name}>Towards {getStationLabel(last)}</option>
										</>
									);
								})()}
							</select>
						</label>

						<div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 10 }}>
							<button
								onClick={() => setModalOpen(false)}
								style={{
									padding: "6px 12px",
									borderRadius: 6,
									border: "none",
									background: theme === "dark" ? "#333" : "#e0e0e0",
									color: theme === "dark" ? "#fff" : "#111",
									cursor: "pointer",
								}}
							>
								{T.cancel}
							</button>
							<button
								onClick={addFavorite}
								disabled={!selectedStop || !selectedTowards}
								style={{
									padding: "6px 12px",
									borderRadius: 6,
									border: "none",
									background:
										!selectedStop || !selectedTowards
											? theme === "dark"
												? "#555"
												: "#ccc"
											: theme === "dark"
												? "#4d91ff"
												: "#1976d2",
									color: "#fff",
									cursor: !selectedStop || !selectedTowards ? "not-allowed" : "pointer",
								}}
							>
								{T.save}
							</button>
						</div>
					</div>
				</div>
			)}
		</section>
	);
}
